import { action } from '@ember/object';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { dropTask, restartableTask } from 'ember-concurrency';
import type { Task } from 'ember-concurrency';
import type FlowModel from 'garaje/models/flow';
import type User from 'garaje/models/user';
import type CurrentLocationService from 'garaje/services/current-location';
import type FlashMessagesService from 'garaje/services/flash-messages';
import type MetricsService from 'garaje/services/metrics';
import type StateService from 'garaje/services/state';
import { parseErrorForDisplay } from 'garaje/utils/flash-promise';
import { GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST } from 'garaje/utils/roles';
import _uniqBy from 'lodash/uniqBy';
import { localCopy } from 'tracked-toolbox';

const INVITE_APPROVAL_PLAN = 'enterprise';

// list of roles that can be searched for to add as notification contacts
const SEARCH_ROLE_LIST = [GLOBAL_ADMIN, LOCATION_ADMIN, RECEPTIONIST];

interface FlowsSectionsSecurityInviteApprovalSignature {
  Args: {
    contacts: User[];
    flow: FlowModel;
    readOnly: boolean;

    /**
     * Optional task to search for users. If not provided, defaults to calling`CurrentLocationService#searchUsers`
     * for users with Global Admin, Location Admin, or Front Desk Admin (Receptionist) roles whose name matches
     * the provided search term.
     */
    searchUsers?: Task<User[], [term: string]>;
  };
}

export default class FlowsSectionsSecurityInviteApproval extends Component<FlowsSectionsSecurityInviteApprovalSignature> {
  @service declare currentLocation: CurrentLocationService;
  @service declare flashMessages: FlashMessagesService;
  @service declare metrics: MetricsService;
  @service declare state: StateService;

  @tracked isDirty = false;
  @tracked isEditing = false;
  @tracked showUnsavedChangesModal = false;
  @localCopy('args.contacts') declare contacts: User[];
  requiredPlan = INVITE_APPROVAL_PLAN;

  get hasAccessToInviteApprovalsFeature(): boolean {
    return !!this.state.features?.canRequireInviteApproval;
  }

  @action
  onUpgradeClicked(): void {
    this.metrics.trackEvent('Visitor Type - Invite Approval Upgrade Clicked', { plan: this.requiredPlan });
  }

  @action
  async disableFeature(): Promise<void> {
    this.args.flow.inviteApprovalsEnabled = false;
    await this.saveTask.perform(false);
  }

  @action
  async enableFeature(): Promise<void> {
    this.args.flow.inviteApprovalsEnabled = true;
    await this.saveTask.perform(true);
  }

  @action
  updateInviteApprovalContacts(contacts: User[]): void {
    this.contacts = [...contacts];
    this.isDirty = true;
  }

  @action
  cancel(confirmed = false): void {
    if (!this.isDirty || confirmed) {
      // if there are no unsaved changes, or user has confirmed to discard their unsaved changes,
      // just reset the state & close the panel
      this.contacts = this.args.flow.inviteApprovalContacts.content;
      this.isDirty = false;
      this.isEditing = false;
      this.showUnsavedChangesModal = false;
    } else if (this.isDirty) {
      // user has unsaved changes; show a modal asking them to confirm
      this.showUnsavedChangesModal = true;
    }
  }

  saveTask = dropTask(async (openAfterSave: boolean | undefined): Promise<void> => {
    try {
      // @ts-expect-error - TS doesn't like the mismatch of AsyncHasMany<User> and User[]
      this.args.flow.inviteApprovalContacts = this.contacts;
      await this.args.flow.save();
      this.flashMessages.showAndHideFlash('success', 'Saved!');
      this.isEditing = !!openAfterSave;
      this.isDirty = false;
    } catch (e) {
      this.flashMessages.showAndHideFlash('error', parseErrorForDisplay(e));
    }
  });

  /**
   * Task for searching for users that can be added as invite approval contacts.
   * Searches for users with Global Admin, Location Admin, or Front Desk Admin/Receptionist roles
   * within the current location whose name matches the provided search term.
   *
   * NOTE: If you change the role checking logic here (SEARCH_ROLE_LIST), you must also update:
   * - app/pods/components/flows/sections/security/approval-chains/component.ts (searchUsers)
   * - app/pods/location-overview/sign-in-flows/show/security/controller.ts (searchUsers)
   */
  searchUsersTask = restartableTask(async (term: string): Promise<User[]> => {
    let users: User[];
    if (this.args.searchUsers) {
      users = await this.args.searchUsers.perform(term);
    } else {
      const userRoles = await this.currentLocation.searchUsers.perform(term, SEARCH_ROLE_LIST, true);
      users = await Promise.all(userRoles.map((role) => role.user));
    }

    const re = new RegExp(`.*${term}.*`, 'i');
    const currentContactsIds = this.contacts.map((contact) => contact.id);

    return _uniqBy(users, 'id') // dedup since multiple roles can point to same user
      .filter((user) => {
        if (currentContactsIds.includes(user.id)) return false; // exclude existing contacts
        return re.test(user.fullName); // only return users whose name matches the search
      });
  });
}
